import { logger } from "@/lib/logger"
import { isClientNetworkError } from "@/lib/networkError"
import {
  getTransportOperationMetadata,
  type TransportOperationName,
} from "@/types_api/backend/transport/operations"

import {
  TransportAuthRedirectError,
  TransportAuthTokenError,
  TransportContextNotReadyError,
  TransportRequestError,
} from "./transportClient"

type TransportOperationKind =
  | "infinite_query"
  | "mutation"
  | "paginated_query"
  | "query"

interface ReportTransportErrorParams {
  error: unknown
  kind: TransportOperationKind
  operation: TransportOperationName
  organizationSlug?: string | null
  // For query-style operations, react-query's `errorUpdatedAt` for the shared
  // cache entry. Every component subscribed to the same failing query observes
  // the same value, so it lets us collapse the per-subscriber report fan-out
  // into a single log per failure. Omitted for mutations, which do not fan out.
  errorUpdatedAt?: number
}

const log = logger("TransportErrorReporting")
const EXPECTED_TRANSPORT_STATUSES = new Set([401, 403, 404, 409])

// A single failed shared query is observed by every component subscribed to it
// (e.g. one MetricTagsCell per row on a large org's metrics page). Each
// subscriber reports the error from its own effect, so without deduplication a
// single transient failure can emit hundreds of identical RUM errors in one
// render commit and trip the JS-error-spike monitor. Collapse reports for the
// same failure (same operation + org + react-query `errorUpdatedAt`) within a
// short window.
const TRANSPORT_ERROR_REPORT_DEDUPE_WINDOW_MS = 5_000
const TRANSPORT_ERROR_REPORT_DEDUPE_MAX_ENTRIES = 500
const recentlyReportedTransportErrors = new Map<string, number>()

function shouldDeduplicateTransportErrorReport(
  dedupeKey: string,
  nowEpochMs: number,
): boolean {
  const lastReportedEpochMs = recentlyReportedTransportErrors.get(dedupeKey)
  if (
    lastReportedEpochMs !== undefined &&
    nowEpochMs - lastReportedEpochMs < TRANSPORT_ERROR_REPORT_DEDUPE_WINDOW_MS
  ) {
    return true
  }

  recentlyReportedTransportErrors.set(dedupeKey, nowEpochMs)
  if (
    recentlyReportedTransportErrors.size >
    TRANSPORT_ERROR_REPORT_DEDUPE_MAX_ENTRIES
  ) {
    for (const [key, reportedEpochMs] of recentlyReportedTransportErrors) {
      if (
        nowEpochMs - reportedEpochMs >=
        TRANSPORT_ERROR_REPORT_DEDUPE_WINDOW_MS
      ) {
        recentlyReportedTransportErrors.delete(key)
      }
    }
  }
  return false
}

function getErrorContext(error: unknown) {
  if (!(error instanceof Error)) {
    return undefined
  }

  return {
    message: error.message,
    name: error.name,
  }
}

function isAbortError(error: unknown) {
  if (typeof error !== "object" || error === null) {
    return false
  }

  const errorRecord = error as { message?: unknown; name?: unknown }
  const message =
    typeof errorRecord.message === "string" ? errorRecord.message : ""
  const name = typeof errorRecord.name === "string" ? errorRecord.name : ""

  return name === "AbortError" || message.includes("The user aborted a request")
}

function shouldReportTransportError(error: unknown) {
  if (isAbortError(error)) {
    return false
  }

  if (error instanceof TransportContextNotReadyError) {
    return false
  }

  if (
    error instanceof TransportRequestError &&
    EXPECTED_TRANSPORT_STATUSES.has(error.status)
  ) {
    return false
  }

  return true
}

function getTransportFailureMessage(
  operation: TransportOperationName,
  error: unknown,
) {
  if (error instanceof TransportAuthTokenError) {
    return `Transport auth token acquisition failed: ${operation}`
  }

  if (error instanceof TransportAuthRedirectError) {
    return `Transport request redirected to sign-in: ${operation} status=${error.status}`
  }

  if (error instanceof TransportRequestError) {
    return `Transport operation failed: ${operation} status=${error.status}`
  }

  return `Transport operation failed: ${operation}`
}

export function reportTransportError({
  error,
  kind,
  operation,
  organizationSlug,
  errorUpdatedAt,
}: ReportTransportErrorParams) {
  if (!shouldReportTransportError(error)) {
    return
  }

  if (errorUpdatedAt != null) {
    const dedupeKey = `${operation}::${organizationSlug ?? ""}::${errorUpdatedAt}`
    if (shouldDeduplicateTransportErrorReport(dedupeKey, Date.now())) {
      return
    }
  }

  const operationDefinition = getTransportOperationMetadata(operation)
  const transportError =
    error instanceof TransportRequestError
      ? {
          code: error.detail?.code,
          detailMessage: error.detail?.message,
          durationMs: error.durationMs,
          status: error.status,
        }
      : undefined
  const baseContext = {
    transportAction: operationDefinition.action,
    transportAuth: operationDefinition.auth,
    transportError,
    transportMethod: operationDefinition.method,
    transportOperation: operation,
    transportOperationKind: kind,
    transportOrganizationRequired: operationDefinition.organizationRequired,
    transportOrganizationSlug: organizationSlug ?? undefined,
    transportPath: operationDefinition.path,
    transportResource: operationDefinition.resource ?? undefined,
    transportRequestLocation: operationDefinition.requestLocation,
  }

  if (error instanceof TransportAuthTokenError) {
    log.warn(getTransportFailureMessage(operation, error), {
      ...baseContext,
      transportAuthTokenError: getErrorContext(error.cause),
    })
    return
  }

  if (error instanceof TransportAuthRedirectError) {
    log.warn(getTransportFailureMessage(operation, error), {
      ...baseContext,
      transportAuthRedirect: true,
      transportAuthRedirectUrl: error.responseUrl,
    })
    return
  }

  // Client-side network failure (offline, DNS, blocked, CORS) — the request never
  // reached us, so it is not a Coval fault. Warn instead of paging the error monitors.
  if (isClientNetworkError(error)) {
    log.warn(getTransportFailureMessage(operation, error), baseContext)
    return
  }

  log.error(getTransportFailureMessage(operation, error), error, baseContext)
}
